/**
 * Calendar Widget Component - Phase 2F
 * Displays key dates for treatment timeline, travel, and appointments
 */
import React, { useState } from 'react';
import { formatDateLocale } from '../utils/dateFormat';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight,
  Plane, Stethoscope, Home, Heart, Hotel, CreditCard
} from 'lucide-react';

const CalendarWidget = ({ 
  events = [], 
  title = "Treatment Calendar",
  compact = false 
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const totalDays = lastDay.getDate();
    
    const days = [];
    
    // Add padding for days before first day of month
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= totalDays; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    const dateStr = formatDateLocale(date);
    return events.filter(event => event.date === dateStr);
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'arrival':
      case 'departure':
      case 'travel':
        return <Plane className="h-3 w-3" />;
      case 'surgery':
      case 'operation':
        return <Heart className="h-3 w-3" />;
      case 'consultation':
      case 'medical':
      case 'recovery':
        return <Stethoscope className="h-3 w-3" />;
      case 'hotel_checkin':
      case 'hotel_checkout':
        return <Hotel className="h-3 w-3" />;
      case 'payment':
        return <CreditCard className="h-3 w-3" />;
      case 'home':
        return <Home className="h-3 w-3" />;
      default:
        return <CalendarIcon className="h-3 w-3" />;
    }
  };

  const getEventColor = (type, status) => {
    if (status === 'completed') return 'bg-green-500';
    if (status === 'in_progress') return 'bg-blue-500';
    
    switch (type) {
      case 'arrival':
      case 'departure':
      case 'travel':
        return 'bg-sky-500';
      case 'surgery':
      case 'operation':
        return 'bg-red-500';
      case 'consultation':
      case 'medical':
      case 'recovery':
        return 'bg-purple-500';
      case 'hotel_checkin':
      case 'hotel_checkout':
        return 'bg-amber-500';
      case 'payment':
        return 'bg-green-500';
      default:
        return 'bg-slate-500';
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return formatDateLocale(date) === formatDateLocale(today);
  };

  const isPast = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const days = getMonthDays(currentDate);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Upcoming events list
  const upcomingEvents = events
    .filter(event => new Date(event.date) >= new Date())
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 5);

  return (
    <Card className={compact ? 'shadow-sm' : 'shadow-lg'}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <CalendarIcon className="h-5 w-5 text-blue-600" />
            {title}
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium min-w-[120px] text-center">
              {currentDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </span>
            <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 mb-4">
          {/* Week day headers */}
          {weekDays.map(day => (
            <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
              {day}
            </div>
          ))}
          
          {/* Day cells */}
          {days.map((date, index) => {
            const dayEvents = getEventsForDate(date);
            const hasEvents = dayEvents.length > 0;
            
            return (
              <div
                key={index}
                className={`
                  relative aspect-square p-1 text-center text-sm rounded-lg
                  ${!date ? 'bg-transparent' : 'hover:bg-slate-50 cursor-pointer'}
                  ${isToday(date) ? 'bg-blue-50 ring-2 ring-blue-500' : ''}
                  ${isPast(date) && !isToday(date) ? 'text-slate-400' : ''}
                `}
              >
                {date && (
                  <>
                    <span className={`${hasEvents ? 'font-semibold' : ''}`}>
                      {date.getDate()}
                    </span>
                    {hasEvents && (
                      <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex gap-0.5">
                        {dayEvents.slice(0, 3).map((event, i) => (
                          <div
                            key={i}
                            className={`w-1.5 h-1.5 rounded-full ${getEventColor(event.type || event.icon, event.status)}`}
                            title={event.title}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Upcoming Events List */}
        {upcomingEvents.length > 0 && (
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-slate-700 mb-3">Upcoming Events</h4>
            <div className="space-y-2">
              {upcomingEvents.map((event, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <div className={`w-8 h-8 rounded-full ${getEventColor(event.type || event.icon, event.status)} flex items-center justify-center text-white`}>
                    {getEventIcon(event.type || event.icon)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{event.title}</p>
                    <p className="text-xs text-slate-500">
                      {formatDateLocale(event.date)}
                      {event.details && ` • ${event.details}`}
                    </p>
                  </div>
                  {event.status && (
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${
                        event.status === 'completed' ? 'bg-green-50 text-green-700 border-green-200' :
                        event.status === 'in_progress' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                        'bg-slate-50 text-slate-600 border-slate-200'
                      }`}
                    >
                      {event.status === 'completed' ? 'Done' : 
                       event.status === 'in_progress' ? 'Active' : 'Upcoming'}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Legend */}
        {!compact && (
          <div className="border-t pt-4 mt-4">
            <div className="flex flex-wrap gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-sky-500" />
                <span className="text-slate-600">Travel</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-slate-600">Surgery</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-purple-500" />
                <span className="text-slate-600">Medical</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-slate-600">Hotel</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-slate-600">Completed</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CalendarWidget;
